import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Table from '../../components/UI/CommonTable/CommonTable';
import Pagination from '../../components/UI/List/Pagination';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import moment from '../../utility/moment-timezone';
import { IMeateryBox } from '../../interfaces/meateryBox';
import { showToastNotification, toFixedThree } from '../../utility/helper';

const MeateryBoxes: React.FC = () => {
    const [boxes, setBoxes] = useState<IMeateryBox[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const navigate = useNavigate();

    useEffect(() => {
        fetchBoxes(currentPage);
    }, [currentPage]);

    const fetchBoxes = async (page: number) => {
        try {
            setLoading(true);
            const res = await callApi(
                `${API_URL_2}${API_ENDPOINT.page}bundles?page=${page}&limit=10`,
                { method: 'GET' }
            );

            setBoxes(res.data);
            setCurrentPage(res.pageInfo.page);
            setTotalPages(res.pageInfo.totalPages);
        } catch (err) {
            console.error('Failed to fetch meatery boxes', err);
        } finally {
            setLoading(false);
        }
    };

    const updateBoxStatus = async (boxId: string, isActive: boolean) => {
        try {
            setLoading(true);
            await callApi(
                `${API_URL_2}${API_ENDPOINT.page}bundles/${boxId}`,
                {
                    method: 'PATCH',
                    data: { isActive: !isActive },
                }
            );
            showToastNotification("success", `Box has been ${isActive ? 'deactivated' : 'activated'} successfully.`);
            fetchBoxes(currentPage);
        } catch (err: any) {
            showToastNotification("danger", err.message ?? 'Failed to update box status.');
            console.error('Failed to update box status', err);
        } finally {
            setLoading(false);
        }
    }
    const columns = [
        {
            header: 'Image',
            accessor: 'images',
            render: (images: string[]) => (
                <img
                    src={images?.[0]}
                    alt="box"
                    style={{ width: 50, height: 50, objectFit: 'cover' }}
                />
            ),
        },
        { header: 'Title', accessor: 'title' },
        { header: 'Slug', accessor: 'slug' },
        {
            header: 'Items',
            accessor: 'items',
            render: (items: any[]) => items?.length ?? 0,
        },
        {
            header: 'Price',
            accessor: 'price',
            render: (price: string) => `BHD ${toFixedThree(price)}`,
        },
        {
            header: 'Status',
            accessor: 'isActive',
            render: (isActive: boolean) => (
                <span className={`badge ${isActive ? 'bg-success' : 'bg-danger'}`}>
                    {isActive ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            header: 'Created At',
            accessor: 'createdAt',
            render: (date: string) =>
                moment(date).format('DD MMM, YYYY'),
        },
    ];
    const actions = [
        {
            icon: 'bi bi-eye',
            color: 'success',
            onClick: (box: IMeateryBox) =>
                navigate(`/meatery-box-details/${box.id}`),
        },
        {
            icon: 'bi bi-pencil',
            color: 'primary',
            onClick: (box: IMeateryBox) =>
                navigate(`/edit-meatery-box/${box.id}`),
        },
        //Inactive the meatery box
        {
            icon: 'bi bi-toggle-on',
            color: 'warning',
            onClick: async (box: IMeateryBox) => updateBoxStatus(box.id, box.isActive),
            show: (box: IMeateryBox) => box.isActive,
            tooltip: 'Deactivate Meatery Box'
        },
        {
            icon: 'bi bi-toggle-off',
            color: 'secondary',
            onClick: async (box: IMeateryBox) => updateBoxStatus(box.id, box.isActive),
            show: (box: IMeateryBox) => !box.isActive,
            tooltip: 'Activate Meatery Box'
        }
    ];
    return (
        <Layout>
            <div className="d-flex justify-content-between align-items-start">
                <h1 className="app-page-title">Meatery Boxes</h1>
                <Link
                    to="/add-meatery-box"
                    className="btn app-btn-primary"
                >
                    + Add Meatery Box
                </Link>
            </div>

            <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                <div className="app-card-body">
                    <Table
                        columns={columns}
                        data={boxes}
                        loading={loading}
                        actions={actions}
                        emptyMessage="No meatery boxes found."
                    />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </div>
        </Layout>
    );
};

export default MeateryBoxes;

